import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SERVER_BASE_URL } from '../../../environments/environment';
import { SeRoute } from '@assets/val/route-constants';

@Injectable({
    providedIn: 'root',
})
export class AisAccountService {
    private readonly httpReqOptions = { withCredentials: true };

    constructor(private http: HttpClient) { }

    getBanks(isPaymentFlow: boolean): Promise<AisBank[]> {
        if (isPaymentFlow) {
            return this.getPisBanks();
        } else {
            return this.getAisBanks();
        }
    }

    getAisBanks(): Promise<AisBank[]> {
        return this.http.get<AisBank[]>(
            `${SERVER_BASE_URL}/ais/se/availableBanks`,
            {
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getPisBanks(): Promise<AisBank[]> {
        return this.http.get<AisBank[]>(
            `${SERVER_BASE_URL}/pis/se/availableBanks`,
            {
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getAccounts(): Promise<GetAisAccountResponse> {
        return this.http.get<GetAisAccountResponse>(
            `${SERVER_BASE_URL}/ais/se/accounts`,
            this.httpReqOptions
        ).toPromise();
    }

    selectAccount(accountId: string): Promise<void> {
        let params = new HttpParams()
            .set('accountId', accountId);

        return this.http.post<void>(
            `${SERVER_BASE_URL}/ais/se/account/select`,
            null,
            {
                params,
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    resetAccountRequirement(): Promise<void> {
        return this.http.post<void>(
            `${SERVER_BASE_URL}/ais/se/flow/resetAccountRequirement`,
            null,
            {
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getAisBankBackNavigationAllowed(): Promise<boolean> {
        return this.http.get<boolean>(
            `${SERVER_BASE_URL}/ais/se/flow/aisBankBackNavigationAllowed`,
            {
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    async setAccountRequirement(): Promise<boolean> {
        try {
            await this.http.post<void>(
                `${SERVER_BASE_URL}/ais/se/flow/setAccountRequirement`,
                null,
                {
                    ...this.httpReqOptions
                }
            ).toPromise();
            return true;
        } catch (e) {
            return false;
        }
    }

    getFinsharkAisUrl(redirectRoute: string, bankSelectionKey: string): Promise<UrlWrapperResponse> {
        let params = new HttpParams()
            .set('redirectRoute', redirectRoute)
            .set('bankSelectionKey', bankSelectionKey)
            .set('locale', 'SE');

        return this.http.get<UrlWrapperResponse>(
            `${SERVER_BASE_URL}/ais/se/finshark/account-check`,
            {
                params: params,
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getFinsharkAccountReport(payload: PayloadGetFinsharkReport): Promise<AccountCheckReportResponse> {
        return this.http.post<AccountCheckReportResponse>(
            `${SERVER_BASE_URL}/ais/se/finshark/account-check/callback`,
            payload,
            this.httpReqOptions
        ).toPromise();
    }
    
    getTinkAisUrl(redirectRoute: string, bankSelectionKey: string): Promise<UrlWrapperResponse> {
        let params = new HttpParams()
            .set('redirectRoute', redirectRoute)
            .set('bankSelectionKey', bankSelectionKey)
            .set('locale', 'sv_SE')
            .set('theme', 'LIGHT');

        return this.http.get<UrlWrapperResponse>(
            `${SERVER_BASE_URL}/ais/se/tink/account-check`,
            {
                params: params,
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getTinkAccountReport(payload: PayloadGetTinkReport): Promise<AccountCheckReportResponse> {
        let params = new HttpParams()
            .set('tinkState', payload.state);

        if (payload.verificationReportId) {
            params = params.set('verificationReportId', payload.verificationReportId);
        }
        if (payload.error) {
            params = params.set('error', payload.error);
        }
        if (payload.errorDisplayMessage) {
            params = params.set('errorDisplayMessage', payload.errorDisplayMessage);
        }
        if (payload.errorReason) {
            params = params.set('errorReason', payload.errorReason);
        }
        if (payload.errorType) {
            params = params.set('errorType', payload.errorType);
        }
        if (payload.message) {
            params = params.set('message', payload.message);
        }
        if (payload.providerName) {
            params = params.set('providerName', payload.providerName);
        }
        if (payload.trackingId) {
            params = params.set('trackingId', payload.trackingId);
        }
        if (payload.resumeToken) {
            params = params.set('resumeToken', payload.resumeToken);
        }

        return this.http.post<AccountCheckReportResponse>(
            `${SERVER_BASE_URL}/ais/se/tink/account-check/callback`,
            null,
            {
                params: params,
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getFinsharkPisUrl(redirectRoute: string, bankSelectionKey: string): Promise<UrlWrapperResponse> {
        let params = new HttpParams()
            .set('redirectRoute', redirectRoute)
            .set('bankSelectionKey', bankSelectionKey)
            .set('locale', 'SE');

        return this.http.get<UrlWrapperResponse>(
            `${SERVER_BASE_URL}/pis/se/finshark/createPayment`,
            {
                params: params,
                ...this.httpReqOptions
            }
        ).toPromise();
    }

    getTinkPisUrl(redirectUrl: SeRoute, bankSelectionKey: string): Promise<UrlWrapperResponse>  {
      throw new Error('Method not implemented.');
    }

    forwardFinsharkPisResult(payload: PayloadGetFinsharkReport): Promise<void> {
        return this.http.post<void>(
            `${SERVER_BASE_URL}/pis/se/finshark/handlePisResult`,
            payload,
            this.httpReqOptions
        ).toPromise();
    }
}

export interface GetAisAccountResponse {
    accounts: AisBankAccount[];
}

export interface AisBankAccount {
    id: string;
    accountName: string;
    displayAccountNumber: string;
    bankName: BankName;
    isSelected: boolean;
    isPrimary: boolean;
    selectedAt: Date;
}

export enum BankName {
    UNKNOWN = 'Unknown',
    SWEDBANK = 'Swedbank',
    SBAB = 'SBAB',
    SPARBANKERNA = 'Sparbankerna',
    SPARBANKEN_SYD = 'Sparbanken Syd',
    DANSKE_BANK = 'Danske Bank',
    ICA = 'ICA Banken',
    HANDELSBANKEN = 'Handelsbanken',
    SEB = 'SEB',
    FOREX = 'Forex',
    SKANDIABANKEN = 'Skandiabanken',
    NORDEA = 'Nordea',
    LANSFORSAKRINGAR = 'Länsförsäkringar',
    ALANDSBANKEN = 'Ålandsbanken',
}

export interface UrlWrapperResponse {
    url: string;
}

export interface AccountCheckReportResponse {
    status: AccountReportStatus;
    aisAccountId: string;
    providerErrorMessage: string;
}

export enum AccountReportStatus {
    SUCCESS = 'SUCCESS',
    INVALID_ACCOUNT_TYPE = 'INVALID_ACCOUNT_TYPE',
    INVALID_ACCOUNT_CURRENCY = 'INVALID_ACCOUNT_CURRENCY',
    INVALID_REPORT = 'INVALID_REPORT',
    USER_CANCELLED = 'USER_CANCELLED',
    INTERNAL_ERROR = 'INTERNAL_ERROR'
}

export interface PayloadGetTinkReport {
    state?: string;
    verificationReportId?: string;
    error?: string;
    errorDisplayMessage?: string;
    errorReason?: string;
    errorType?: string;
    message?: string;
    providerName?: string;
    trackingId?: string;
    resumeToken?: string;
};

export interface PayloadGetFinsharkReport {
    verifiedAccountId?: string; // Only for AIS
    authorizationId?: string;
    sessionId?: string;
    result?: string;
    message?: string;
    requestContext?: string;
    resumeToken?: string;
};

export interface AisBank {
    id: string;
    name: BankName;
    logoUrl: string;
    ordinal: number;
    activeProvider: AisProvider;
    activePisProvider: AisProvider;
    finsharkSelectionKey: string;
    tinkSelectionKey: string;
}

export enum AisProvider {
    TINK = 'TINK',
    FINSHARK = 'FINSHARK'
}
